"use client"
import { Store } from "@tanstack/store"

// Module-level variable for range tracking suppression (not in store to avoid re-renders)
let rangeTrackingSuppressed = false
let scrollListenerCleanup: (() => void) | null = null

export interface ProductPaginationState {
  currentPage: number
  pageSize: number
  pendingExpandSectionId: string | null
  pendingProductId: string | null
  /** Product index (0-based) to navigate to after page mount — set from request page table */
  pendingNavigateProductIndex: number | null
  /** Cache for field path lookups — keyed by path with index stripped (e.g. "products.*.itemDescription") */
  fieldPathCache: Map<string, unknown>
}

const initialState: ProductPaginationState = {
  currentPage: 1,
  pageSize: 15,
  pendingExpandSectionId: null,
  pendingProductId: null,
  pendingNavigateProductIndex: null,
  fieldPathCache: new Map(),
}

export const productPaginationStore = new Store<ProductPaginationState>(
  initialState,
)

export const productPaginationActions = {
  setPage: (page: number) => {
    productPaginationStore.setState((s) => ({ ...s, currentPage: page }))
  },

  /** Navigate to a product by global index — sets page + pending expand */
  navigateToProduct: (productIndex: number) => {
    const state = productPaginationStore.state
    const targetPage = Math.floor(productIndex / state.pageSize) + 1
    const sectionId = `${productIndex + 1}`

    productPaginationStore.setState((s) => ({
      ...s,
      currentPage: targetPage,
      pendingExpandSectionId: targetPage !== s.currentPage ? sectionId : null,
    }))

    // Same page — expand + scroll synchronously
    if (targetPage === state.currentPage) {
      const el = document.getElementById(sectionId)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      }
      // Return sectionId so the hook can expand it
      return sectionId
    }
    return null
  },

  /** Called after cross-page render completes */
  clearPendingExpand: () => {
    productPaginationStore.setState((s) => ({
      ...s,
      pendingExpandSectionId: null,
    }))
  },

  setPendingProduct: (id: string | null) => {
    productPaginationStore.setState((s) => ({ ...s, pendingProductId: id }))
  },

  /** Set a product index to navigate to after the products page mounts */
  setPendingNavigate: (index: number | null) => {
    productPaginationStore.setState((s) => ({
      ...s,
      pendingNavigateProductIndex: index,
    }))
  },

  /**
   * Suppress Virtuoso rangeChanged from updating currentSection.
   * Call this when user manually clicks an accordion to prevent the layout
   * shift from overriding their selection.
   * Suppression clears when user scrolls manually (wheel/touch).
   */
  suppressRangeTrackingBriefly: () => {
    // Clean up any existing listener
    scrollListenerCleanup?.()

    rangeTrackingSuppressed = true

    // Clear suppression when user scrolls manually
    const clearSuppression = () => {
      rangeTrackingSuppressed = false
      scrollListenerCleanup?.()
      scrollListenerCleanup = null
    }

    // Listen for user-initiated scroll (wheel or touch)
    const onWheel = () => clearSuppression()
    const onTouchMove = () => clearSuppression()

    window.addEventListener("wheel", onWheel, { once: true, passive: true })
    window.addEventListener("touchmove", onTouchMove, {
      once: true,
      passive: true,
    })

    scrollListenerCleanup = () => {
      window.removeEventListener("wheel", onWheel)
      window.removeEventListener("touchmove", onTouchMove)
    }

    // Fallback: clear after 5s max in case user doesn't scroll
    setTimeout(() => {
      if (rangeTrackingSuppressed) {
        clearSuppression()
      }
    }, 5000)
  },

  /** Check if range tracking is currently suppressed */
  isRangeTrackingSuppressed: () => rangeTrackingSuppressed,

  reset: () => {
    const pending = productPaginationStore.state.pendingProductId
    const pendingNav = productPaginationStore.state.pendingNavigateProductIndex
    productPaginationStore.setState(() => ({
      ...initialState,
      pendingProductId: pending,
      pendingNavigateProductIndex: pendingNav,
      fieldPathCache: new Map(),
    }))
  },

  /**
   * Cached field path lookup. Strips product index from path to create a
   * generic cache key (e.g. "products.3.itemDescription" → "products.*.itemDescription").
   * Same field across different products hits cache instead of scanning the registry.
   */
  cachedFieldLookup: <T>(
    fieldPath: string,
    lookupFn: (path: string) => T,
  ): T => {
    const cacheKey = fieldPath.replace(/products\.\d+\./, "products.*.")
    const cache = productPaginationStore.state.fieldPathCache
    if (cache.has(cacheKey)) return cache.get(cacheKey) as T
    const result = lookupFn(fieldPath)
    cache.set(cacheKey, result)
    return result
  },
}

export const productPaginationSelectors = {
  selectCurrentPage: (s: ProductPaginationState) => s.currentPage,
  selectPendingExpand: (s: ProductPaginationState) => s.pendingExpandSectionId,
  selectPendingProduct: (s: ProductPaginationState) => s.pendingProductId,
  selectPendingNavigate: (s: ProductPaginationState) =>
    s.pendingNavigateProductIndex,
}
